/**
 * PR-V0c / GRIPS-INTERACTION PR-1/2 — builds + memoises the grip-drag start
 * handler (`buildGripStartHandler`) and wires the click-arm ↔ `hotGripStore`
 * bridge (arm / disarm / Enter-Space mode-cycle / commit echo) + the
 * unmount-abort cleanup. Extracted out of `Scene3DStage.tsx` to keep that
 * file under the repo's file-size gate (RP4 split pattern — same as
 * `usePolarDraftAidEffects` / `useOsnapBindings` / `useStageRendererHandle`).
 *
 * All refs are OWNED BY THE CALLER (Scene3DStage.tsx passes its own
 * `liveCameraRef` / `overlayShellRef` / `osnapBindingsRef` /
 * `spatialIndexRefForHover` / `activeSpaceIdForHoverRef` / `selectedIdsRef` —
 * every one of those is ALSO read by other, unrelated parts of that
 * component, so they cannot move here) — this hook owns only the two refs
 * exclusively private to the grip-drag state machine itself
 * (`gripDragRef` / `gripActiveFinishRef`).
 */

import * as THREE from "three";
import { useEffect, useMemo, useRef } from "react";

import { gripToHotRef } from "../grips/gripsOverlay";
import {
  clearArmedGrip,
  cycleArmedEditMode,
  setArmedGrip,
} from "../grips/hotGripStore";
import type { Scene3DStageProps } from "../Scene3DStageProps";
import type { OsnapBindings } from "../stageInteractions";
import type { SpatialIndex } from "../types";
import {
  buildGripStartHandler,
  type GripDragState,
  type GripStartHandlerBundle,
} from "./StageGripDrag";
import { useGripModeEcho } from "./useGripModeEcho";

interface UseGripStartBundleArgs {
  liveCameraRef: { current: THREE.Camera | null };
  overlayShellRef: { current: HTMLDivElement | null };
  osnapBindingsRef: { current: OsnapBindings | null };
  spatialIndexRef: { current: SpatialIndex | null };
  activeSpaceIdRef: { current: string };
  selectedIdsRef: { current: readonly string[] };
  onGripDrag: Scene3DStageProps["onGripDrag"];
  onGripDragStart: Scene3DStageProps["onGripDragStart"];
  onGripDragEnd: Scene3DStageProps["onGripDragEnd"];
  onGripDragCancel: Scene3DStageProps["onGripDragCancel"];
  onGripTransformCommit: Scene3DStageProps["onGripTransformCommit"];
  onGripModeEcho: Scene3DStageProps["onGripModeEcho"];
}

/** Returns the stable `handleGripStart` pointer handler for the grip glyphs. */
export function useGripStartBundle(
  args: UseGripStartBundleArgs,
): GripStartHandlerBundle["handleGripStart"] {
  const {
    liveCameraRef,
    overlayShellRef,
    osnapBindingsRef,
    spatialIndexRef,
    activeSpaceIdRef,
    selectedIdsRef,
    onGripDrag,
    onGripDragStart,
    onGripDragEnd,
    onGripDragCancel,
    onGripTransformCommit,
    onGripModeEcho,
  } = args;
  // PR-V0c — grip-drag-private state. Codex P1 (#317): `gripActiveFinishRef`
  // lives OUTSIDE the memoised bundle so a callback-prop identity change
  // mid-drag rebuilds the bundle WITHOUT stranding or aborting the live drag.
  const gripDragRef = useRef<GripDragState | null>(null);
  const gripActiveFinishRef = useRef<
    ((mode: "commit" | "abort") => void) | null
  >(null);
  // GRIPS-INTERACTION PR-2 — command-line echo (arm/cycle/commit); returns a
  // STABLE commit-echo relay for `onGripArmedCommit` below.
  const handleGripArmedCommit = useGripModeEcho(onGripModeEcho);
  const gripStartBundle = useMemo(
    () =>
      buildGripStartHandler({
        liveCameraRef,
        overlayShellRef,
        onGripDragProp: onGripDrag,
        ...(onGripDragStart !== undefined ? { onGripDragStart } : {}),
        ...(onGripDragEnd !== undefined ? { onGripDragEnd } : {}),
        ...(onGripDragCancel !== undefined ? { onGripDragCancel } : {}),
        // GRIPS-INTERACTION PR-1 — click-arm ↔ armed-grip model bridge. A CLICK
        // on a grip warms it into the modal "** STRETCH **" phase (armed
        // indicator); the second click commits. PR-2 adds the Enter/Space
        // mode-cycle relay + commit echo. All STABLE module setters / the
        // `useGripModeEcho`-returned callback (not memo deps below).
        onGripArm: (grip) => setArmedGrip(gripToHotRef(grip)),
        onGripDisarm: clearArmedGrip,
        onGripCycleMode: cycleArmedEditMode,
        onGripArmedCommit: handleGripArmedCommit,
        ...(onGripTransformCommit !== undefined
          ? { onGripTransformCommit }
          : {}),
        gripDragRef,
        activeFinishRef: gripActiveFinishRef,
        // GRIPS-INTERACTION (re-audit P2) — live OSNAP bindings + spatial
        // index so a grip drag clamps to running object snaps (AutoCAD
        // parity). #350 @codex — active-space id for the snap same-space
        // filter. GRIPS-INTERACTION PR-2 — live selection for a non-Stretch
        // commit. All STABLE refs read at pointermove/commit-time, not memo
        // inputs.
        osnapBindingsRef,
        spatialIndexRef,
        activeSpaceIdRef,
        selectedIdsRef,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- liveCameraRef/overlayShellRef/osnapBindingsRef/spatialIndexRef/activeSpaceIdRef/selectedIdsRef/handleGripArmedCommit are STABLE (refs, or a useCallback with empty deps) read at pointermove/commit-time, not memo inputs; rebuilding the bundle on their identity would needlessly churn the React.memo'd children.
    [onGripDrag, onGripDragStart, onGripDragEnd, onGripDragCancel, onGripTransformCommit],
  );
  // Codex P1 (#317) — abort an in-progress grip drag ONLY on real component
  // unmount, never on bundle/callback churn (empty-dep, reads the stable ref).
  useEffect(
    () => () => {
      if (gripActiveFinishRef.current) gripActiveFinishRef.current("abort");
    },
    [],
  );
  return gripStartBundle.handleGripStart;
}
